import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { fmtAUD, fmtDate } from '@/lib/format';
import { toast, Toaster } from 'sonner';
import { Plus, X, LayoutDashboard, Package, Factory, Building2, ShoppingBag, DollarSign } from 'lucide-react';
import AdminInventory from './AdminInventory';
import AdminManufacturers from './AdminManufacturers';
import AdminPricing from './AdminPricing';

const ORDER_STATUSES = ['submitted', 'paid', 'fulfilled', 'cancelled'];

const TABS = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'manufacturers', label: 'Manufacturers', icon: Factory },
    { id: 'academies', label: 'Academies', icon: Building2 },
];

export default function AdminDashboard() {
    const [tab, setTab] = useState(() => {
        const hash = window.location.hash.replace('#', '');
        return TABS.some((t) => t.id === hash) ? hash : 'overview';
    });

    useEffect(() => {
        window.location.hash = tab;
    }, [tab]);

    return (
        <div data-testid="admin-dashboard">
            <Toaster theme="dark" />
            <div className="font-mono-data text-xs uppercase text-brand tracking-widest mb-2">HQ Control Room</div>
            <h1 className="font-display text-4xl uppercase tracking-tighter mb-6">Operations Portal</h1>

            <nav className="border-hairline mb-8 flex overflow-x-auto" data-testid="admin-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            data-testid={`tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-2 px-5 py-3 font-display uppercase text-xs tracking-wider transition-colors border-r border-zinc-800 last:border-r-0 ${
                                active
                                    ? 'bg-brand text-black'
                                    : 'text-zinc-400 hover:text-white hover:bg-surface'
                            }`}
                        >
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </nav>

            {tab === 'overview' && <Overview goTo={setTab} />}
            {tab === 'orders' && <OrdersTab />}
            {tab === 'pricing' && <AdminPricing />}
            {tab === 'inventory' && <AdminInventory />}
            {tab === 'manufacturers' && <AdminManufacturers />}
            {tab === 'academies' && <AcademiesTab />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
function Overview({ goTo }) {
    const [orders, setOrders] = useState([]);
    const [academies, setAcademies] = useState([]);
    const [stockSummary, setStockSummary] = useState(null);
    const [manufacturers, setManufacturers] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get('/orders'),
            api.get('/academies'),
            api.get('/stock/summary'),
            api.get('/manufacturers'),
        ]).then(([o, a, s, m]) => {
            setOrders(o.data);
            setAcademies(a.data);
            setStockSummary(s.data);
            setManufacturers(m.data);
        });
    }, []);

    const stats = {
        total: orders.length,
        submitted: orders.filter((o) => o.status === 'submitted').length,
        paid: orders.filter((o) => o.status === 'paid').length,
        fulfilled: orders.filter((o) => o.status === 'fulfilled').length,
        revenue: orders.filter((o) => o.payment_status === 'paid').reduce((s, o) => s + o.subtotal, 0),
        academies: academies.length,
    };
    const recent = orders.slice(0, 6);

    const tile = (label, value, hint, accent, target) => (
        <button onClick={() => target && goTo(target)}
                className={`p-5 text-left border-r border-zinc-800 last:border-r-0 transition-colors ${target ? 'hover:bg-surface' : 'cursor-default'}`}>
            <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">{label}</div>
            <div className={`font-display text-3xl ${accent || ''}`}>{value}</div>
            {hint && <div className="font-mono-data text-[10px] uppercase text-zinc-500 mt-1">{hint}</div>}
        </button>
    );

    return (
        <div className="space-y-10">
            <section className="grid grid-cols-2 md:grid-cols-4 border-hairline">
                {tile('Revenue (paid)', fmtAUD(stats.revenue), `${stats.paid} paid orders`, 'text-brand')}
                {tile('Awaiting', stats.submitted, 'Action needed', 'text-amber-400', 'orders')}
                {tile('Fulfilled', stats.fulfilled, 'Shipped', null, 'orders')}
                {tile('Academies', stats.academies, null, null, 'academies')}
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 border-hairline">
                {tile('SKUs tracked', stockSummary?.total_skus ?? '—', null, null, 'inventory')}
                {tile('Units in stock', stockSummary?.total_units ?? '—', null, 'text-brand', 'inventory')}
                {tile('Low stock', stockSummary?.low_stock ?? '—', 'Reorder soon', 'text-amber-400', 'inventory')}
                {tile('Out of stock', stockSummary?.out_of_stock ?? '—', 'Production required', 'text-danger', 'inventory')}
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-2xl uppercase tracking-tighter">Recent Orders</h2>
                        <button onClick={() => goTo('orders')}
                                className="font-mono-data text-[10px] uppercase text-zinc-400 hover:text-brand">
                            View all →
                        </button>
                    </div>
                    <div className="border-hairline">
                        {recent.length === 0 && (
                            <div className="p-6 text-center font-mono-data text-xs text-zinc-500">No orders yet.</div>
                        )}
                        {recent.map((o) => (
                            <div key={o.id} className="grid grid-cols-12 items-center px-5 py-3 border-b border-zinc-800/60 last:border-0">
                                <div className="col-span-5 font-display uppercase text-sm">{o.academy_name}</div>
                                <div className="col-span-3 font-mono-data text-xs text-zinc-400">{fmtDate(o.created_at)}</div>
                                <div className="col-span-2 text-right font-mono-data text-xs">{fmtAUD(o.subtotal)}</div>
                                <div className="col-span-2 text-right">
                                    <span className="font-mono-data text-[10px] uppercase text-zinc-400">{o.status}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-2xl uppercase tracking-tighter">Suppliers</h2>
                        <button onClick={() => goTo('manufacturers')}
                                className="font-mono-data text-[10px] uppercase text-zinc-400 hover:text-brand">
                            Manage →
                        </button>
                    </div>
                    <div className="border-hairline">
                        {manufacturers.length === 0 && (
                            <div className="p-6 text-center font-mono-data text-xs text-zinc-500">No manufacturers yet.</div>
                        )}
                        {manufacturers.map((m) => (
                            <div key={m.id} className="px-5 py-4 border-b border-zinc-800/60 last:border-0">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="font-display uppercase text-sm">{m.name}</div>
                                    <span className="font-mono-data text-[10px] uppercase text-brand">{m.category}</span>
                                </div>
                                <div className="font-mono-data text-xs text-zinc-400">
                                    {m.country || '—'} · {m.lead_time_weeks}wk · {m.product_slugs.length} products
                                </div>
                                {m.contact_email && (
                                    <div className="font-mono-data text-xs text-zinc-500">{m.contact_email}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Orders tab (full-table view)
// ---------------------------------------------------------------------------
function OrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const reload = async () => {
        const r = await api.get('/orders');
        setOrders(r.data);
    };

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/orders/${id}`, { status });
            toast.success(`Order → ${status}`);
            reload();
        } catch {
            toast.error('Update failed');
        }
    };

    if (loading) return <div className="text-zinc-400 font-mono-data">Loading…</div>;
    const filtered = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

    return (
        <section>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl uppercase tracking-tighter">All Orders</h2>
                <div className="flex gap-1">
                    {['all', ...ORDER_STATUSES].map((s) => (
                        <button key={s} onClick={() => setFilter(s)}
                                className={`font-mono-data text-[10px] uppercase px-3 py-1.5 border ${
                                    filter === s ? 'border-brand text-brand' : 'border-zinc-800 text-zinc-500 hover:text-white'
                                }`}>
                            {s}
                        </button>
                    ))}
                </div>
            </div>
            <div className="border-hairline">
                <div className="grid grid-cols-12 font-mono-data text-[10px] uppercase text-zinc-500 px-5 py-3 border-b border-zinc-800 bg-surface">
                    <div className="col-span-2">Order</div>
                    <div className="col-span-3">Academy</div>
                    <div className="col-span-2">Date</div>
                    <div className="col-span-2 text-right">Total</div>
                    <div className="col-span-3 text-right">Status</div>
                </div>
                {filtered.length === 0 && (
                    <div className="p-8 text-center text-zinc-500 font-mono-data text-sm">No orders match.</div>
                )}
                {filtered.map((o) => (
                    <div key={o.id} className="grid grid-cols-12 items-center px-5 py-3 border-b border-zinc-800/60">
                        <div className="col-span-2 font-mono-data text-xs">{o.id.slice(0, 8)}…</div>
                        <div className="col-span-3 font-display uppercase text-sm">{o.academy_name}</div>
                        <div className="col-span-2 font-mono-data text-xs text-zinc-400">{fmtDate(o.created_at)}</div>
                        <div className="col-span-2 text-right font-mono-data text-sm">{fmtAUD(o.subtotal)}</div>
                        <div className="col-span-3 text-right">
                            <select
                                data-testid={`order-status-select-${o.id}`}
                                value={o.status}
                                onChange={(e) => updateStatus(o.id, e.target.value)}
                                className="bg-surface border border-zinc-800 px-2 py-1 font-mono-data text-xs uppercase">
                                {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Academies tab
// ---------------------------------------------------------------------------
function AcademiesTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [draft, setDraft] = useState({ code: '', name: '', contact_email: '', password: '' });

    const reload = async () => {
        const r = await api.get('/academies');
        setItems(r.data);
    };

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post('/academies', draft);
            toast.success(`Academy ${draft.name} created`);
            setShowNew(false);
            setDraft({ code: '', name: '', contact_email: '', password: '' });
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to create');
        }
    };

    const toggleActive = async (a) => {
        try {
            await api.patch(`/academies/${a.id}`, { active: !a.active });
            toast.success(`${a.name} ${!a.active ? 'enabled' : 'disabled'}`);
            reload();
        } catch {
            toast.error('Update failed');
        }
    };

    if (loading) return <div className="text-zinc-400 font-mono-data">Loading…</div>;

    return (
        <section>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl uppercase tracking-tighter">Academies</h2>
                <button data-testid="admin-add-academy-btn"
                        onClick={() => setShowNew(!showNew)}
                        className="bg-brand hover:bg-brand-hover text-black px-4 py-2 font-display uppercase tracking-wider text-sm inline-flex items-center gap-2">
                    {showNew ? <X size={14} /> : <Plus size={14} />}
                    {showNew ? 'Cancel' : 'Add Academy'}
                </button>
            </div>

            {showNew && (
                <form onSubmit={create} data-testid="admin-new-academy-form"
                      className="border-hairline p-6 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input data-testid="new-academy-code" required placeholder="Code (e.g. bondi)"
                           value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                           className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                    <input data-testid="new-academy-name" required placeholder="Display name"
                           value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                           className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                    <input data-testid="new-academy-email" type="email" placeholder="Contact email"
                           value={draft.contact_email} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
                           className="bg-surface border border-zinc-800 px-3 py-2 text-white" />
                    <div className="flex gap-2">
                        <input data-testid="new-academy-password" required placeholder="Initial password"
                               value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                               className="flex-1 bg-surface border border-zinc-800 px-3 py-2 text-white" />
                        <button data-testid="new-academy-submit" type="submit"
                                className="bg-brand hover:bg-brand-hover text-black px-4 py-2 font-display uppercase tracking-wider text-sm">
                            Save
                        </button>
                    </div>
                </form>
            )}

            <div className="border-hairline">
                <div className="grid grid-cols-12 font-mono-data text-[10px] uppercase text-zinc-500 px-5 py-3 border-b border-zinc-800 bg-surface">
                    <div className="col-span-2">Code</div>
                    <div className="col-span-4">Name</div>
                    <div className="col-span-3">Email</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1 text-right">Action</div>
                </div>
                {items.map((a) => (
                    <div key={a.id} className="grid grid-cols-12 items-center px-5 py-3 border-b border-zinc-800/60">
                        <div className="col-span-2 font-mono-data text-xs">{a.code}</div>
                        <div className="col-span-4 font-display uppercase text-sm">{a.name}</div>
                        <div className="col-span-3 font-mono-data text-xs text-zinc-400">{a.contact_email || '—'}</div>
                        <div className="col-span-2">
                            <span className={`inline-block px-2 py-1 border font-mono-data text-[10px] uppercase ${a.active ? 'text-brand border-brand/40' : 'text-zinc-500 border-zinc-700'}`}>
                                {a.active ? 'Active' : 'Disabled'}
                            </span>
                        </div>
                        <div className="col-span-1 text-right">
                            <button data-testid={`toggle-academy-${a.code}`} onClick={() => toggleActive(a)}
                                    className="font-mono-data text-[10px] uppercase text-zinc-400 hover:text-brand">
                                {a.active ? 'Disable' : 'Enable'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
